import { useEffect } from "react";
import { track } from "@/lib/analytics";

/**
 * Lightweight analytics bridge for the Living Frequency Field.
 * Listens to the field's custom events and forwards them to the
 * project analytics pipeline. Attention events are throttled to
 * avoid flooding when a visitor dwells on a section.
 */
export function FieldAnalytics() {
  useEffect(() => {
    if (typeof window === "undefined") return;

    let lastAttention = 0;
    let surgeCount = 0;
    let signatureCount = 0;
    let idleCount = 0;
    let attentionCount = 0;

    const onSurge = (e: Event) => {
      const ce = e as CustomEvent<{ x?: number; y?: number }>;
      surgeCount += 1;
      track("field_surge", {
        x: Math.round(ce.detail?.x ?? -1),
        y: Math.round(ce.detail?.y ?? -1),
        n: surgeCount,
      });
    };

    const onSignature = (e: Event) => {
      const ce = e as CustomEvent<{ x?: number; y?: number }>;
      signatureCount += 1;
      track("field_ankh_signature", {
        x: Math.round(ce.detail?.x ?? -1),
        y: Math.round(ce.detail?.y ?? -1),
        n: signatureCount,
      });
    };

    const onIdle = () => {
      idleCount += 1;
      track("field_idle_enter", { n: idleCount });
    };

    const onAttention = (e: Event) => {
      const now = performance.now();
      if (now - lastAttention < 4000) return; // throttle 4s
      lastAttention = now;
      const ce = e as CustomEvent<{ x?: number; y?: number; section?: string }>;
      attentionCount += 1;
      track("field_attention", {
        section: ce.detail?.section ?? null,
        n: attentionCount,
      });
    };

    const onBreath = (e: Event) => {
      const ce = e as CustomEvent<{ x?: number; y?: number }>;
      track("first_gate_breath", {
        x: Math.round(ce.detail?.x ?? -1),
        y: Math.round(ce.detail?.y ?? -1),
      });
    };

    window.addEventListener("kaf:surge", onSurge);
    window.addEventListener("kaf:ankh-signature", onSignature);
    window.addEventListener("kaf:idle", onIdle);
    window.addEventListener("kaf:attention", onAttention);
    window.addEventListener("kaf:breath", onBreath);

    return () => {
      window.removeEventListener("kaf:surge", onSurge);
      window.removeEventListener("kaf:ankh-signature", onSignature);
      window.removeEventListener("kaf:idle", onIdle);
      window.removeEventListener("kaf:attention", onAttention);
      window.removeEventListener("kaf:breath", onBreath);
    };
  }, []);

  return null;
}
